import React, { useEffect, useState } from 'react';
import { get } from '../api/http';
import { belongsToStage } from '../constants/demandStages';

/**
 * DemandSelector - switch between demands from a stage screen.
 *
 * Two changes from the original: it no longer returns null when the current stage
 * has no demands (which left the screen with no way to change selection and no
 * explanation), and the per-comparison console.log calls are gone. When the stage
 * is empty it offers every demand instead, flagged as such.
 *
 * @param {string} currentDemandId - selected demand id
 * @param {string} stage - stage to filter by ('triage', 'discovery', ...)
 * @param {function} onDemandChange - called with the new demand id
 */
const DemandSelector = ({ currentDemandId, stage, onDemandChange }) => {
  const [demands, setDemands] = useState([]);
  const [inStageCount, setInStageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const result = await get('/demands/list', { query: { limit: 200 } });
        if (cancelled) return;

        const all = result?.demands || [];
        // Match on the canonical stage rather than a raw startsWith. startsWith got
        // TRIAGE_PENDING and DISCOVERY_COMPLETE right by luck, but it can never match
        // an alias whose name doesn't begin with its stage — ENHANCED_DISCOVERY on
        // the POC page, AI_REQUIREMENTS on Requirements — so demands carrying those
        // legacy values were invisible on the screen that owned them.
        const target = String(stage || '').toLowerCase();
        const inStage = target ? all.filter((d) => belongsToStage(d.current_stage, target)) : all;

        setInStageCount(inStage.length);
        setDemands(inStage.length ? inStage : all);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [stage]);

  if (loading) {
    return <span style={{ fontSize: 13, color: 'var(--cares-text-note)' }}>Loading demands…</span>;
  }
  if (error) {
    return <span style={{ fontSize: 13, color: 'var(--cares-orange)' }}>Could not load demands: {error}</span>;
  }
  if (!demands.length) {
    return <span style={{ fontSize: 13, color: 'var(--cares-text-note)' }}>No demands yet.</span>;
  }

  const showingAll = inStageCount === 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <label style={{ fontSize: '14px', color: 'var(--cares-text-label)', fontWeight: '500' }}>Demand:</label>
      <select
        value={currentDemandId || ''}
        onChange={(e) => onDemandChange(e.target.value)}
        style={{
          padding: '8px 32px 8px 12px',
          borderRadius: '8px',
          border: '1px solid var(--cares-line-strong)',
          backgroundColor: 'var(--cares-surface-card)',
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--cares-text-1)',
          cursor: 'pointer',
          outline: 'none',
          minWidth: '220px',
        }}
      >
        {!currentDemandId && <option value="">Select a demand…</option>}
        {demands.map((demand) => (
          <option key={demand.demand_id} value={demand.demand_id}>
            {demand.demand_id} — {(demand.title || 'Untitled').slice(0, 40)}
            {(demand.title || '').length > 40 ? '…' : ''}
          </option>
        ))}
      </select>
      <span
        title={showingAll
          ? `No demands are currently in the ${stage} stage; showing all demands.`
          : undefined}
        style={{
          fontSize: '12px',
          color: showingAll ? 'var(--cares-amber)' : 'var(--cares-text-note)',
          backgroundColor: showingAll ? 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))' : 'var(--cares-surface-board)',
          padding: '4px 8px',
          borderRadius: '6px',
          fontWeight: '500',
        }}
      >
        {showingAll
          ? `none in ${stage} — showing all ${demands.length}`
          : `${inStageCount} in ${String(stage).charAt(0).toUpperCase()}${String(stage).slice(1)}`}
      </span>
    </div>
  );
};

export default DemandSelector;
